import firebase from "firebase/compat/app";

import AccountService, { userLimitations } from "./AccountService";
import { observable } from "mobx";
import { RouterStore } from "mobx-react-router";

export type ContactResult =
  | "INVALID_ID"
  | "INVALID_NAME"
  | "INVALID_PHONE"
  | "INVALID_EMAIL"
  | "INVALID_USER"
  | "UNKNOWN_ERROR"
  | "ADD_SUCCESS"
  | "UPDATE_SUCCESS"
  | "DELETE_SUCCESS"
  | "DELETE_CONTACTS_SUCCESS"
  | "CONTACT_IS_EXIST"
  | "CONTACT_NOT_FOUNDED"
  | "GET_SUCCESS"
  | "ADD_LOCATIONS_SUCCESS"
  | "REMOVE_LOCATIONS_SUCCESS";

export type TemplateResult =
  | "INVALID_ID"
  | "INVALID_NAME"
  | "INVALID_USER"
  | "UNKNOWN_ERROR"
  | "ADD_SUCCESS"
  | "UPDATE_SUCCESS"
  | "DELETE_SUCCESS"
  | "TEMPLATE_IS_EXIST"
  | "TEMPLATE_NOT_FOUNDED"
  | "GET_SUCCESS"
  | "ADD_LOCATIONS_SUCCESS";

export type ContactData = {
  contactId: string;
  name: string;
  phone: string;
  email?: string;
  locations: Array<string>;
  createdAt: string;
};

export type TemplateData = {
  id?: string;
  template_id?: string;
  name: string;
  html_content: string | any;
  content?: string | any;
  subject: HTMLElement | any;
  updated_at?: string;
  type?: string;
};

function validateEmail(email: string): boolean {
  if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
    return true;
  }
  return false;
}

export default class ContactService {
  private _accountService: AccountService;
  private _db: firebase.firestore.Firestore = firebase.firestore();
  private _funcs = firebase.functions();

  templatesHandler = this._funcs.httpsCallable("templatesHandler");

  //private _selectedContacts : Array<ContactData> = []

  @observable
  private _selectedContacts = new Array<ContactData>();

  @observable
  private _selectedContact: ContactData | any = null;

  @observable
  private _contacts = new Array<ContactData | any>();

  public get allContacts(): Array<ContactData> {
    return this._contacts;
  }
  public get selectedContacts(): Array<ContactData> {
    return this._selectedContacts;
  }
  public get selectedContact(): ContactData {
    return this._selectedContact;
  }

  /*************** Templates methods ************/

  @observable
  private _sgTemplates = new Array<TemplateData | any>();

  @observable
  private _fsTemplates = new Array<TemplateData | any>();

  @observable
  private _selectedTemplate: TemplateData | any = null;

  public get allSgTemplates(): Array<TemplateData> {
    return this._sgTemplates;
  }

  public get allFsTemplates(): Array<TemplateData> {
    return this._fsTemplates;
  }

  public get selectedTemplate(): TemplateData {
    return this._selectedTemplate;
  }

  /********************** End ********************/

  constructor(account: AccountService) {
    this._accountService = account;
  }

  async findContact(
    contactName: string,
    previousContanctName: string = ""
  ): Promise<ContactResult> {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return;
      }
      const result = await this._db
        .collection("allContacts")
        .doc(`contact-${user.uid}`)
        .collection("contacts")
        .where("name", "==", contactName)
        .get();
      const foundedContact = result.docs.map((doc) => doc.data());
      if (foundedContact.length !== 0) {
        return foundedContact[0]?.name === previousContanctName
          ? "CONTACT_IS_EXIST"
          : "CONTACT_NOT_FOUNDED";
      } else {
        return "CONTACT_NOT_FOUNDED";
      }
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async getContact(contactId: string): Promise<ContactResult> {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return "INVALID_USER";
      }
      const result = await this._db
        .collection("allContacts")
        .doc(`contact-${user.uid}`)
        .collection("contacts")
        .where("contactId", "==", contactId)
        .get();
      const contact = result.docs.map((doc) => doc.data())[0];
      this._selectedContact = contact;
      return "GET_SUCCESS";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async addContact(contact: ContactData): Promise<ContactResult> {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return;
      }
      // check if name alserady exists
      const contactFoundedResult = await this.findContact(contact.name);
      if (contactFoundedResult === "CONTACT_IS_EXIST") {
        return "CONTACT_IS_EXIST";
      } else {
        await this._db
          .collection("allContacts")
          .doc(`contact-${user.uid}`)
          .collection("contacts")
          .doc(contact.contactId)
          .set(contact);
        return "ADD_SUCCESS";
      }
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async updateContact(
    contact: ContactData,
    contactId: string
  ): Promise<ContactResult> {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return;
      }
      // check if name alserady exists
      const contactFoundedResult = await this.findContact(contact.name);
      await this._db
        .collection("allContacts")
        .doc(`contact-${user.uid}`)
        .collection("contacts")
        .doc(contactId)
        .set(contact);
      return "UPDATE_SUCCESS";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async deleteContact(contactId: string): Promise<ContactResult> {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return "INVALID_USER";
      }
      if (!contactId) {
        return "INVALID_ID";
      }
      const result = await this._db
        .collection("allContacts")
        .doc(`contact-${user.uid}`)
        .collection("contacts")
        .doc(contactId)
        .delete();
      return "DELETE_SUCCESS";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async deleteContacts(selectedContacts: any[]): Promise<ContactResult> {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return "INVALID_USER";
      }
      await Promise.all(
        selectedContacts.map(
          async (contact) =>
            await this._db
              .collection("allContacts")
              .doc(`contact-${user.uid}`)
              .collection("contacts")
              .doc(contact.contactId)
              .delete()
        )
      );
      return "DELETE_CONTACTS_SUCCESS";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async fetchAllContacts(): Promise<ContactResult> {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return "INVALID_USER";
      }
      const result = await this._db
        .collection("allContacts")
        .doc(`contact-${user.uid}`)
        .collection("contacts")
        .get();
      const contactsData = await result.docs.map((doc) => doc.data());
      this._contacts = contactsData;
      return "GET_SUCCESS";
    } catch (error) {
      console.log("error", error);
      return "UNKNOWN_ERROR";
    }
  }

  async addLocationsToContacts(
    locationsToAdd: any[],
    selectedContacts: any[]
  ): Promise<ContactResult> {
    const user = this._accountService.user;
    if (!user) {
      return "INVALID_USER";
    }

    try {
      await Promise.all(
        selectedContacts.map(async (contact) => {
          const locationsId = contact.locations.map((loc) => loc.locationId);
          const newLocations = locationsToAdd.filter(
            ({ locationId }) => !locationsId.includes(locationId)
          );
          await this._db
            .collection("allContacts")
            .doc(`contact-${user.uid}`)
            .collection("contacts")
            .doc(`${contact.contactId}`)
            .set({
              ...contact,
              locations: [...contact.locations, ...newLocations],
            });
        })
      );
      return "ADD_LOCATIONS_SUCCESS";
    } catch (err) {
      console.log("err", err);
    }
  }

  async removeLocationsFromContacts(
    locationsToRemove: any[],
    selectedContacts: any[]
  ): Promise<ContactResult> {
    const user = this._accountService.user;
    if (!user) {
      return "INVALID_USER";
    }

    try {
      await Promise.all(
        selectedContacts.map(async (contact) => {
          const locationsId = locationsToRemove.map((loc) => loc.locationId);
          return await this._db
            .collection("allContacts")
            .doc(`contact-${user.uid}`)
            .collection("contacts")
            .doc(`${contact.contactId}`)
            .set({
              ...contact,
              locations: [
                ...contact.locations.filter(
                  ({ locationId }) => !locationsId.includes(locationId)
                ),
              ],
            });
        })
      );
      return "REMOVE_LOCATIONS_SUCCESS";
    } catch (err) {
      console.log("err", err);
    }
  }

  /***************** Templates Actions ******************/

  async getTemplates(): Promise<any> {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return "INVALID_USER";
      }
      const customData = {
        userId: user.uid,
      };
      const result = await this._db
        .collection("allTemplates")
        .doc(`template-${user.uid}`)
        .collection("templates")
        .get();
      const fsTemplatesData = result.docs.map((doc) => doc.data());
      this._fsTemplates = fsTemplatesData;
      // get templates from sendgrid
      // save the results in the this._sgTemplates
      const sgTemplates = await this.templatesHandler({
        type: "GET_TEMPLATES_VERSION",
        customData,
      });
      if (sgTemplates.data) {
        this._sgTemplates = sgTemplates.data;
      } else {
        this._sgTemplates = [];
      }
      return "GET_SUCCESS";
    } catch (error) {
      console.log("error", error);
      return "UNKNOWN_ERROR";
    }
  }

  async findTemplate(templateName: string): Promise<TemplateResult> {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return;
      }
      const result = await this._db
        .collection("allTemplates")
        .doc(`template-${user.uid}`)
        .collection("templates")
        .where("name", "==", templateName)
        .get();
      const foundedTemplate = result.docs.map((doc) => doc.data());
      if (foundedTemplate.length !== 0) {
        return "TEMPLATE_IS_EXIST";
      } else {
        return "TEMPLATE_NOT_FOUNDED";
      }
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async getTemplate(templateId: string): Promise<TemplateResult | any> {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return "INVALID_USER";
      }
      const result = await this._db
        .collection("allTemplates")
        .doc(`template-${user.uid}`)
        .collection("templates")
        .where("templateId", "==", templateId)
        .get();
      const template = result.docs.map((doc) => doc.data())[0];
      return template;
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async addTemplate(
    userTemplates: any[],
    template: TemplateData,
    type: string
  ): Promise<TemplateResult> {
    const { name, subject, html_content, content } = template;
    try {
      const user = this._accountService.user;
      if (user == null) {
        return;
      }
      const customData = {
        userId: user.uid,
        userTemplates,
        templateName: name,
        subject,
        htmlContent: html_content,
      };
      const result = await this.templatesHandler({
        type: "ADD_TEMPLATE_VERSION",
        customData,
      });
      if (result.data) {
        const { template_id: templateId, id: versionId } = result.data;
        await this.addEmailTemplate(
          name,
          subject,
          templateId,
          versionId,
          content,
          type
        );
        return "ADD_SUCCESS";
      } else return "TEMPLATE_IS_EXIST";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async addEmailTemplate(
    name: string,
    subject: string,
    templateId: string,
    versionId: string,
    content: string,
    type: string
  ) {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return;
      }
      await this._db
        .collection("allTemplates")
        .doc(`template-${user.uid}`)
        .collection("templates")
        .doc(templateId)
        .set({
          name,
          subject,
          templateId,
          versionId,
          content,
          type,
        });
      return "ADD_SUCCESS";
    } catch (err) {
      console.log({ err });
    }
  }

  async updateTemplate(
    template: TemplateData,
    templateId: string,
    versionId: string,
    type: string
  ): Promise<TemplateResult> {
    try {
      const user = this._accountService.user;
      const { name, subject, html_content, content } = template;
      if (user == null) {
        return;
      }
      const customData = {
        userId: user.uid,
        versionId,
        templateId,
        body: {
          name,
          subject,
          htmlContent: html_content,
        },
      };
      const result = await this.templatesHandler({
        type: "UPDATE_TEMPLATE_VERSION",
        customData,
      });

      if (result) {
        const updatedAt = result.data.updatedAt;
        const res = await this.updateEmailTemplate(
          name,
          subject,
          templateId,
          versionId,
          content,
          type,
          updatedAt
        );
        return "UPDATE_SUCCESS";
      }
    } catch (error) {
      console.log({ error });
      return "UNKNOWN_ERROR";
    }
  }

  async updateEmailTemplate(
    name: string,
    subject: string,
    templateId: string,
    versionId: string,
    content: string,
    type: string,
    updatedAt: string
  ) {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return;
      }
      await this._db
        .collection("allTemplates")
        .doc(`template-${user.uid}`)
        .collection("templates")
        .doc(templateId)
        .set({
          name,
          subject,
          templateId,
          versionId,
          content,
          type,
          updatedAt,
        });
      return "UPDATE_SUCCESS";
    } catch (err) {
      console.log({ err });
    }
  }

  async deleteTemplate(
    templateId: string,
    versionId: string
  ): Promise<TemplateResult> {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return "INVALID_USER";
      }
      if (!templateId || !versionId) {
        return "INVALID_ID";
      }
      // await this._db
      //   .collection('allTemplates')
      //   .doc(`template-${user.uid}`)
      //   .collection('templates')
      //   .doc(templateId)
      //   .delete();
      const customData = {
        versionId,
        templateId,
      };
      const codeStatusRes = await this.templatesHandler({
        type: "DELETE_TEMPLATE_VERSION",
        customData,
      });
      if (codeStatusRes.data === 204) {
        await this.deleteEmailTemplate(templateId);
      }
      return "DELETE_SUCCESS";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async deleteEmailTemplate(templateId: string): Promise<TemplateResult> {
    try {
      const user = this._accountService.user;
      if (user == null) {
        return "INVALID_USER";
      }
      if (!templateId) {
        return "INVALID_ID";
      }
      const res = await this._db
        .collection("allTemplates")
        .doc(`template-${user.uid}`)
        .collection("templates")
        .doc(templateId)
        .delete();
    } catch (err) {
      console.log("err", err);
      return "UNKNOWN_ERROR";
    }
  }
}
